import { DomHelpers } from "@/utils/DomHelpers";

/** Slot: video or div.slot-image — index must match .artist-wrap order */
type SlotElement = HTMLVideoElement | HTMLElement;

function isVideoEl(el: SlotElement): el is HTMLVideoElement {
  return el.tagName === 'VIDEO';
}

/**
 * Artist section: hover/focus shows corresponding slot (video or image); on mobile, scroll determines active slot
 */
export class ArtistVideoController {
  private readonly section: HTMLElement;
  private readonly wrap: HTMLElement;
  private readonly items: Element[];
  private readonly slots: SlotElement[];
  private activeIndex = -1;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;
  private mobileOn = false;
  private ticking = false;

  constructor(section: HTMLElement) {
    this.section = section;
    const w = DomHelpers.qs<HTMLElement>(section, '.artist-video');
    const itemList = DomHelpers.qsAll(section, '.artist-list .artist-wrap');
    const slotList = w ? DomHelpers.qsAll<SlotElement>(w, '.slot') : [];
    if (!w || slotList.length < 1 || itemList.length < 1)
      throw new Error('ArtistVideoController: missing .artist-video or .artist-wrap or .slot');
    if (slotList.length !== itemList.length)
      throw new Error(`ArtistVideoController: slot count (${slotList.length}) must match artist-wrap count (${itemList.length})`);
    this.wrap = w;
    this.items = itemList;
    this.slots = slotList;
  }

  init(): void {
    this.slots.forEach((slot) => {
      if (isVideoEl(slot)) {
        slot.muted = true;
        slot.playsInline = true;
        slot.setAttribute('muted', '');
        slot.setAttribute('playsinline', '');
      }
    });
    this.wrap.style.opacity = '0';
    this.slots.forEach((slot) => {
      slot.classList.remove('is-active');
      if (isVideoEl(slot)) {
        try {
          slot.pause();
        } catch (_) {
          // ignore
        }
      }
    });

    this.items.forEach((li, i) => {
      li.addEventListener('mouseenter', () => this.showIndex(i));
      li.addEventListener('focusin', () => this.showIndex(i));
    });
    this.section.addEventListener('mouseleave', this.hideAll);
    this.section.addEventListener('focusout', () => {
      if (!this.section.contains(document.activeElement)) this.hideAll();
    });

    const mq = window.matchMedia('(max-width: 767.98px)');
    const handleMQ = (e: MediaQueryListEvent | MediaQueryList): void => {
      if (e.matches) this.enableMobile();
      else this.disableMobile();
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handleMQ);
    } else if (typeof (mq as MediaQueryList & { addListener?: (e: MediaQueryListEvent | MediaQueryList) => void }).addListener === 'function') {
      (mq as MediaQueryList & { addListener: (e: MediaQueryListEvent | MediaQueryList) => void }).addListener(handleMQ);
    }
    handleMQ(mq);
  }

  private showIndex(i: number): void {
    if (i === this.activeIndex || i < 0 || i >= this.slots.length) return;
    const slot = this.slots[i];
    this.wrap.style.opacity = '1';
    this.slots.forEach((s, j) => s.classList.toggle('is-active', j === i));
    if (isVideoEl(slot)) {
      slot.play().catch(() => {});
    }
    if (this.pauseTimer) clearTimeout(this.pauseTimer);
    this.pauseTimer = setTimeout(() => {
      this.slots.forEach((s, j) => {
        if (j !== i && isVideoEl(s)) {
          try {
            s.pause();
          } catch (_) {
            // ignore
          }
        }
      });
    }, 480);
    this.activeIndex = i;
  }

  private hideAll = (): void => {
    if (this.pauseTimer) clearTimeout(this.pauseTimer);
    this.wrap.style.opacity = '0';
    this.slots.forEach((slot) => {
      slot.classList.remove('is-active');
      if (isVideoEl(slot)) {
        try {
          slot.pause();
        } catch (_) {
          // ignore
        }
      }
    });
    this.activeIndex = -1;
  };

  private computeCenterIndex(): void {
    const vh = window.innerHeight;
    const mid = vh / 2;
    const s = this.section.getBoundingClientRect();
    if (s.bottom < 0 || s.top > vh) {
      this.hideAll();
      return;
    }
    let best = -1;
    let bestDist = Infinity;
    this.items.forEach((li, i) => {
      const r = li.getBoundingClientRect();
      if (r.bottom <= 0 || r.top >= vh) return;
      const liMid = r.top + r.height / 2;
      const d = Math.abs(liMid - mid);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    if (best !== -1) this.showIndex(Math.min(best, this.slots.length - 1));
  }

  private onScroll = (): void => {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.computeCenterIndex();
    });
  };

  private enableMobile(): void {
    if (this.mobileOn) return;
    this.mobileOn = true;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    this.computeCenterIndex();
  }

  private disableMobile(): void {
    if (!this.mobileOn) return;
    this.mobileOn = false;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.hideAll();
  }
}
